import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { vendorApi } from '../api/vendorApi'

interface LowStockAlertProps {
  vendorId: string
}

export function LowStockAlert({ vendorId }: LowStockAlertProps) {
  const [expanded, setExpanded] = useState(false)
  const { data } = useQuery({
    queryKey: ['vendor-low-stock', vendorId],
    queryFn: () => vendorApi.fetchLowStock(vendorId),
    enabled: !!vendorId,
    refetchInterval: 60000,
  })

  const items = data?.items ?? []
  if (items.length === 0) return null

  const visibleItems = expanded ? items : items.slice(0, 3)
  const hasMore = items.length > 3

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-5 h-5 text-amber-600" />
        <h3 className="font-semibold text-amber-900">Low Stock Alert</h3>
        <Badge variant="outline" className="text-amber-700 border-amber-300 text-xs">
          {items.length} item{items.length !== 1 ? 's' : ''}
        </Badge>
      </div>
      <div className="space-y-2">
        {visibleItems.map((item: { variantId: string; productName: string; size: string; color: string; stockQty: number }) => (
          <div key={item.variantId} className="flex items-center justify-between text-sm">
            <span className="text-amber-900">
              {item.productName} — {item.size}/{item.color}
            </span>
            <Badge variant="outline" className={item.stockQty === 0 ? 'text-red-700 border-red-300' : 'text-amber-700 border-amber-300'}>
              {item.stockQty} remaining
            </Badge>
          </div>
        ))}
      </div>
      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-2 text-amber-700 hover:text-amber-900"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <><ChevronUp className="w-4 h-4 mr-1" /> Show less</>
          ) : (
            <><ChevronDown className="w-4 h-4 mr-1" /> Show {items.length - 3} more</>
          )}
        </Button>
      )}
    </div>
  )
}
